import React, { Children, useCallback, useEffect, useState, useRef } from "react"
import { TimelineLite, Draggable, TweenLite, Power2 } from "gsap/all"
import { useMediaQuery } from "react-responsive"
import { round, sumBy } from "lodash"
import PropTypes from "prop-types"
import gsap from "gsap"

// Components
import SwitchButton from "Components/chartsLibrary/Map/components/button-switch"
import ZoomButton from "Components/chartsLibrary/Map/components/button-zoom"
import Legende from "Components/chartsLibrary/Map/components/legende"
import Tooltip from "Components/chartsLibrary/Map/components/tooltip"
import TooltipPRNSN from "Components/chartsLibrary/Map/tooltipPRNSN"

import ExportMenu from "Components/ExportMenu"

// Style
import { MainContainer } from "./index.style.js"

// Utils
import { downloadScreenShotFile, getImgInPDF } from "Utils/screenshotPage"
import { getObjectToExport } from "Utils/widgetsUtils"
import { exportDatas } from "Utils/global"
import {
  generateLegendValue,
  transformCP,
  getNewFilterValues,
  haveSameValue,
} from "Components/chartsLibrary/Map/utils"

// SVG paths & fake Data
import {
  fakeDataMapDepartemental,
  fakeDataAsiePacifique,
  fakeDataRegionalNew,
  fakeDataMapRegional,
  fakeDataMapWorld,
  fakeDataEurope,
  SVGpathAsiePacifique,
  SVGpathDepartemental,
  SVGpathRegionalNew,
  SVGpathRegional,
  SVGpathEurope,
  SVGpathWorld,
} from "Components/chartsLibrary/Map/svgPath"

import { formatNumberDisplayed } from "../utils/global.js"

const MapSVG = ({
  primaryActionColor,
  legendLabelWeight,
  bulletBorderSize,
  numberDecimal,
  decimalSeparator,
  showSpaceForThousand,
  visibilityLegend,
  enableExportMenu,
  legendLabelColor,
  legendLabelSize,
  withChoicePath,
  colorSetTheme,
  scaleDuration,
  zoomAnimation,
  categoryData,
  tooltipColor,
  zoomButtons,
  legendLabel,
  bulletColor,
  isDraggable,
  isZoomable,
  scaleValue,
  bulletView,
  mapChoice,
  dataChart,
  valueData,
  fieldsData,
  mapVue,
  widgetTraduction,
  widgetTitle,
  legendSpecific,
  tooltipSpecificPRNSN,
  mapPRNSN,
  // Filter
  onSelectFilterValue,
  filterType,
  requestLoad,
  languageId,
}) => {
  /**
   * @description Global state
   */
  const [selectedPath, setSelectedPath] = useState(mapChoice)
  const [legendValue, setLegendValue] = useState(legendSpecific)
  const [itemSelected, setItemSelected] = useState("")
  const [countRefresh, setCountRefresh] = useState(0)
  const [svg, setSvg] = useState(null)
  const [filterListValues, setFilterListValues] = useState([])

  /**
   * @description Tooltip state
   */
  const [openTooltip, setOpenTooltip] = useState(false)
  const [tooltipValue, setTooltipValue] = useState({
    value: 0,
    valueToDisplay: "",
  })
  const [clientX, setClientX] = useState()
  const [clientY, setClientY] = useState()

  // ***************************** //
  //             VARIABLES         //
  // ***************************** //
  const isTablette = useMediaQuery({ minWidth: 751 }, { maxWidth: 1224 })
  const isMobile = useMediaQuery({ maxWidth: 750 })

  const exportMenuPosition = { top: "9px", right: "52px" }
  const exportMenuId = `exportMenu${Math.random()}`
  const mapId = `map${Math.random()}`

  // ***************************** //
  //             Refs              //
  // ***************************** //
  const VIEWPORT = useRef(null)
  const SVG = useRef(null)

  // ***************************** //
  //          Life Cycle           //
  // ***************************** //
  useEffect(() => {
    const actual = SVG?.current
    setSvg(actual)

    VIEWPORT?.current.addEventListener("wheel", onWheel)

    if (legendSpecific.length === 0)
      setLegendValue(
        generateLegendValue(dataChart, valueData, selectedPath, colorSetTheme, showSpaceForThousand)
      )

    if (fakeDataAsiePacifique?.length) {
      const makeFakeData = fakeDataAsiePacifique?.map(item => {
        return {
          ...item,
          value: round(Math.random() * (10000 - 100) + 100),
        }
      })
    }
  }, [])

  useEffect(
    () => () => {
      setCountRefresh(0)
    },
    []
  )

  useEffect(() => {
    if (openTooltip) {
      setTooltipValue(previousObj => {
        var formatedValue = formatNumberDisplayed(
          previousObj?.value,
          numberDecimal,
          showSpaceForThousand,
          decimalSeparator
        )
        return {
          ...previousObj,
          valueToDisplay: formatedValue,
        }
      })
    }
  }, [numberDecimal, decimalSeparator, showSpaceForThousand])

  useEffect(() => {
    setSelectedPath(mapChoice)
  }, [mapChoice])

  useEffect(() => {
    if (legendSpecific.length === 0) {
      setLegendValue(
        generateLegendValue(dataChart, valueData, selectedPath, colorSetTheme, showSpaceForThousand)
      )
    }
  }, [selectedPath])

  useEffect(() => {
    if (legendSpecific.length === 0) {
      setLegendValue(
        generateLegendValue(dataChart, valueData, selectedPath, colorSetTheme, showSpaceForThousand)
      )
    }
  }, [dataChart, colorSetTheme, showSpaceForThousand])

  if (isDraggable) {
    gsap.registerPlugin(Draggable)
    Draggable.create(VIEWPORT?.current, {
      type: "x,y",
      edgeResistance: 0.65,
      bounds: SVG?.current,
      inertia: true,
    })
  }

  let zoom = {
    animation: new TimelineLite(),
    scaleFactor: scaleValue,
    duration: scaleDuration,
    ease: Power2?.easeOut,
  }

  // ***************************** //
  //           Functions           //
  // ***************************** //
  /**
   * @description Créer un point de référence sur la map
   * (exemple pour afficher le tooltip, il faut créer un point au click avec un axe x et y)
   */
  const createSvgPoint = (x, y) => {
    let point = SVG?.current?.createSVGPoint()

    point.x = x
    point.y = y

    return point.matrixTransform(SVG?.current?.getScreenCTM().inverse())
  }

  /**
   * @description Changer de cartographie
   * @param {String} mapChoice
   * @requires svg format
   */
  const getPaths = mapChoice => {
    switch (mapChoice) {
      case "franceReg":
        return SVGpathRegional
      case "franceRegNew":
        return SVGpathRegionalNew
      case "franceDep":
        return SVGpathDepartemental
      case "world":
        return SVGpathWorld
      case "europe":
        return SVGpathEurope
      case "asiePacifique":
        return SVGpathAsiePacifique
      default:
        return SVGpathDepartemental
    }
  }

  /**
   * @description GET data (pour le moment fake)
   * @param {String} mapChoice
   */
  const getData = mapChoice => {
    switch (mapChoice) {
      case "franceReg":
        return fakeDataMapRegional
      case "franceRegNew":
        return fakeDataRegionalNew
      case "franceDep":
        return fakeDataMapDepartemental
      case "world":
        return fakeDataMapWorld
      case "europe":
        return fakeDataEurope
      case "asiePacifique":
        return fakeDataAsiePacifique
      default:
        return fakeDataMapDepartemental
    }
  }

  /**
   * @description Zoom à la molette
   */
  const onWheel = event => {
    if (isZoomable) {
      let actualView = SVG?.current?.viewBox?.baseVal

      disableWindowScroll(event)

      // if () => Bloquer le zoom in maximum (event.wheelDelta 120 = zoom in)
      // else if () => Bloquer le zoom out maximum (event.wheelDelta -120 = zoom out)
      if (actualView?.width >= 200 && event?.wheelDelta == 120) {
        let normalized
        const delta = 120

        if (delta) {
          normalized = delta % 120 == 0 ? delta / 120 : delta / 12
        } else {
          delta = event?.deltaY || event?.detail || 0
          normalized = -(delta % 3 ? delta * 10 : delta / 3)
        }

        const scaleDelta = normalized > 0 ? 1 / zoom?.scaleFactor : zoom?.scaleFactor
        const startPoint = createSvgPoint(event?.clientX, event?.clientY)

        const fromVars = {
          ease: zoom?.ease,
          x: actualView?.x,
          y: actualView?.y,
          width: actualView?.width,
          height: actualView?.height,
        }

        actualView.x -= (startPoint?.x - actualView?.x) * (scaleDelta - 1)
        actualView.y -= (startPoint?.y - actualView?.y) * (scaleDelta - 1)
        actualView.width *= scaleDelta
        actualView.height *= scaleDelta

        if (zoomAnimation) {
          zoom.animation = TweenLite.from(actualView, zoom?.duration, fromVars)
        }
      } else if (actualView?.width <= 550 && event?.wheelDelta == -120) {
        let normalized
        const delta = -120

        if (delta) {
          normalized = delta % 120 == 0 ? delta / 120 : delta / 12
        } else {
          delta = event?.deltaY || event?.detail || 0
          normalized = -(delta % 3 ? delta * 10 : delta / 3)
        }

        const scaleDelta = normalized > 0 ? 1 / zoom?.scaleFactor : zoom?.scaleFactor
        const startPoint = createSvgPoint(event?.clientX, event?.clientY)

        const fromVars = {
          ease: zoom?.ease,
          x: actualView?.x,
          y: actualView?.y,
          width: actualView?.width,
          height: actualView?.height,
        }

        actualView.x -= (startPoint?.x - actualView?.x) * (scaleDelta - 1)
        actualView.y -= (startPoint?.y - actualView?.y) * (scaleDelta - 1)
        actualView.width *= scaleDelta
        actualView.height *= scaleDelta

        if (zoomAnimation) {
          zoom.animation = TweenLite.from(actualView, zoom?.duration, fromVars)
        }
      }
    }
  }

  /**
   * @description Zoom via bouton +
   */
  const onZoomIn = useCallback(() => {
    let actualView = SVG?.current?.viewBox?.baseVal

    // Bloquer le zoom in maximum
    if (actualView?.width >= 100) {
      let normalized
      const delta = 120

      if (delta) {
        normalized = delta % 120 == 0 ? delta / 120 : delta / 12
      } else {
        delta = -100 || 0 || 0
        normalized = -(delta % 3 ? delta * 10 : delta / 3)
      }

      const scaleDelta = normalized > 0 ? 1 / zoom?.scaleFactor : zoom?.scaleFactor
      // const startPoint = createSvgPoint(600, 400)

      const fromVars = {
        ease: zoom?.ease,
        x: actualView?.x,
        y: actualView?.y,
        width: actualView?.width,
        height: actualView?.height,
      }

      actualView.x -= (350 - actualView?.x) * (scaleDelta - 1)
      actualView.y -= (150 - actualView?.y) * (scaleDelta - 1)
      actualView.width *= scaleDelta
      actualView.height *= scaleDelta

      if (zoomAnimation) {
        zoom.animation = TweenLite.from(actualView, zoom?.duration, fromVars)
      }
    }
  }, [])

  /**
   * @description Zoom via bouton -
   */
  const onZoomOut = useCallback(() => {
    let actualView = SVG?.current?.viewBox?.baseVal

    // Bloquer le zoom out maximum
    if (actualView?.width <= 611) {
      let normalized
      const delta = -120

      if (delta) {
        normalized = delta % 120 == 0 ? delta / 120 : delta / 12
      } else {
        delta = 100 || 0 || 0
        normalized = -(delta % 3 ? delta * 10 : delta / 3)
      }

      const scaleDelta = normalized > 0 ? 1 / zoom?.scaleFactor : zoom?.scaleFactor
      // const startPoint = createSvgPoint(600, 400)

      const fromVars = {
        ease: zoom?.ease,
        x: actualView?.x,
        y: actualView?.y,
        width: actualView?.width,
        height: actualView?.height,
      }

      actualView.x -= (350 - actualView?.x) * (scaleDelta - 1)
      actualView.y -= (150 - actualView?.y) * (scaleDelta - 1)
      actualView.width *= scaleDelta
      actualView.height *= scaleDelta

      if (zoomAnimation) {
        zoom.animation = TweenLite.from(actualView, zoom?.duration, fromVars)
      }
    }
  }, [])

  /**
   * @description Sélectionner un item de la map (1 item = <path /> ou autre balise svg)
   * @param {String} path
   * @param {Number} value
   */
  const onSelectItem = (event, path, value) => {
    setItemSelected(path)
    var formatedValue = formatNumberDisplayed(
      value,
      numberDecimal,
      showSpaceForThousand,
      decimalSeparator
    )
    setTooltipValue({ value: value, valueToDisplay: formatedValue })

    const svgPointRef = createSvgPoint(event?.clientX, event?.clientY)

    if (isTablette || isMobile) {
      if (svgPointRef?.x >= 450 && svgPointRef?.y >= 450) {
        // Création d'un point de référence
        setClientX(svgPointRef?.x - 350)
        setClientY(svgPointRef?.y - 350)
      } else if (svgPointRef?.x >= 450) {
        // Création d'un point de référence
        setClientX(svgPointRef?.x - 350)
        setClientY(svgPointRef?.y)
      } else if (svgPointRef?.y >= 450) {
        // Création d'un point de référence
        setClientX(svgPointRef?.x)
        setClientY(svgPointRef?.y - 350)
      } else {
        // Création d'un point de référence
        setClientX(svgPointRef?.x)
        setClientY(svgPointRef?.y)
      }
    } else {
      // Création d'un point de référence
      setClientX(svgPointRef?.x)
      setClientY(svgPointRef?.y)
    }

    setOpenTooltip(true)
  }

  const onFilterChange = (e, path) => {
    let newValues

    if (selectedPath === "franceRegNew" || mapChoice === "franceRegNew") {
      newValues = path.codePostal.map(cp => ({ value: cp, label: cp }))
    } else {
      newValues = { value: path.codePostal[0], label: path.title }
    }

    const newFilterList = getNewFilterValues(filterListValues, filterType, path, newValues)
    setFilterListValues(newFilterList)
    onSelectFilterValue(newFilterList)
  }

  const onFilterClear = () => {
    setFilterListValues([])
    onSelectFilterValue([])
  }

  const preventDefault = e => {
    e.preventDefault()
  }

  const disableWindowScroll = e => {
    const wheelEvent = "onwheel" in document.createElement("div") ? "wheel" : "mousewheel"

    window.addEventListener("DOMMouseScroll", preventDefault(e), false) // older FF
    window.addEventListener(wheelEvent, preventDefault(e), false) // modern desktop
  }

  const onCloseTooltip = () => {
    setClientX()
    setClientY()
    setOpenTooltip(false)
  }

  const hideElement = element => {
    element.style.display = "none"
  }

  const exportPNG = () => {
    const title = widgetTitle.replace(/ /g, "_")
    const exportMenu = document.getElementById(exportMenuId)
    hideElement(exportMenu)
    // take the screen and export
    downloadScreenShotFile(mapId, title)
    // display again the menu
    setTimeout(() => exportMenu.removeAttribute("style"), 10) // ten milliseconds is invisible for the user
  }

  const getImgAndSaveAsPDF = () => {
    const exportMenu = document.getElementById(exportMenuId)
    const title = widgetTitle.replace(/ /g, "_")
    // hide the menu to not show him in the export
    exportMenu.style.display = "none"
    // take the screen and export
    getImgInPDF(mapId, title)
    // display again the menu
    setTimeout(() => exportMenu.removeAttribute("style"), 10) // ten milliseconds is invisible for the user
  }

  const exportMap = (fileType = "csv") => {
    if (dataChart.length) {
      const title = widgetTitle.replace(/ /g, "_")
      const header = Object.keys(dataChart[0])

      const objectToExport = getObjectToExport(
        fieldsData,
        header,
        dataChart,
        languageId,
        requestLoad
      )

      exportDatas(objectToExport, fileType, title)
    } else {
      alert("no datas to export")
    }
  }

  return (
    <MainContainer
      visibilityLegend={visibilityLegend}
      withChoicePath={withChoicePath}
      isMobile={isMobile}
      id={mapId}
    >
      {withChoicePath && (
        <SwitchButton
          primaryActionColor={primaryActionColor}
          label1={widgetTraduction?.DEPARTEMENT}
          label2={widgetTraduction?.REGION}
          setSelectedPath={setSelectedPath}
          selectedPath={selectedPath}
        />
      )}
      {mapVue === "filter" && filterListValues.length > 0 && (
        <div className="clear_filter_button" title={widgetTraduction?.CLEAR_FILTER}>
          <i className="fas fa-xmark" onClick={onFilterClear}></i>
        </div>
      )}
      {openTooltip &&
        (tooltipSpecificPRNSN ? (
          <TooltipPRNSN
            onCloseTooltip={onCloseTooltip}
            itemSelected={itemSelected}
            tooltipValue={tooltipValue.valueToDisplay}
            tooltipColor={tooltipColor}
            clientX={clientX}
            clientY={clientY}
          />
        ) : (
          <Tooltip
            onCloseTooltip={onCloseTooltip}
            itemSelected={itemSelected}
            tooltipValue={tooltipValue.valueToDisplay}
            tooltipColor={tooltipColor}
            clientX={clientX}
            clientY={clientY}
          />
        ))}
      {zoomButtons && <ZoomButton onZoomOut={onZoomOut} onZoomIn={onZoomIn} />}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        width="100%"
        height="100%"
        viewBox="0 0 612 585"
        id="map_SVG_container"
        ref={SVG}
      >
        <g ref={VIEWPORT}>
          {Children.toArray(
            getPaths(selectedPath).map(path => {
              let legendInformations = []
              let sum
              let sum2
              let sum3

              if (dataChart?.length) {
                if (mapVue === "bulletBckgrd") {
                  let dataMap = []

                  dataChart?.map(data => {
                    path?.codePostal?.filter(cp => {
                      if (
                        data[categoryData]?.toString()?.length === 1 ||
                        data[categoryData]?.toString()?.length === 4 ||
                        data[categoryData]?.toString()?.length === 5
                      ) {
                        if (transformCP(data[categoryData]) === cp) dataMap.push(data)
                      } else {
                        if (data[categoryData]?.toString() === cp) dataMap.push(data)
                      }
                    })
                  })

                  sum = valueData && sumBy(dataMap, valueData?.[0]) // CDESI
                  sum2 = valueData && sumBy(dataMap, valueData?.[1]) // PDESI
                  sum3 = valueData && sumBy(dataMap, valueData?.[2]) // PDIPR
                } else {
                  let dataMap = []

                  dataChart?.map(data => {
                    path?.codePostal?.filter(cp => {
                      if (
                        data[categoryData]?.toString()?.length === 1 ||
                        data[categoryData]?.toString()?.length === 4 ||
                        data[categoryData]?.toString()?.length === 5
                      ) {
                        if (transformCP(data[categoryData]) === cp) dataMap.push(data)
                      } else {
                        if (data[categoryData]?.toString() === cp) dataMap.push(data)
                      }
                    })
                  })

                  sum = valueData && sumBy(dataMap, valueData?.[0])

                  legendInformations = legendValue?.filter(
                    legend => sum >= legend?.firstValue && sum <= legend?.secondValue
                  )
                }
              } else {
                // constante Fake Data (Map/utils.js)
                sum = getData(selectedPath).filter(data => data?.id === path?.id)?.[0]?.value
                // GET code couleur par rapport au valeur de la légende (Map/utils.js)
                legendInformations = legendValue.filter(
                  legend => sum >= legend?.firstValue && sum <= legend?.secondValue
                )
              }

              return (() => {
                switch (mapVue) {
                  case "bckgrColor":
                    return (
                      <g transform={path?.transform}>
                        <path
                          onClick={e => onSelectItem(e, path, sum)}
                          className="map_item_svg"
                          fill={legendInformations[0]?.color || "#ECECEC"}
                          strokeWidth="1px"
                          stroke="#FFF"
                          title={path?.title}
                          id={path?.id}
                          d={path?.d}
                        />
                      </g>
                    )
                  case "bullet":
                    return path?.isCircle ? (
                      <circle
                        fill={bulletColor || "#ECECEC"}
                        cx={path?.x}
                        cy={path?.y}
                        r={sum <= 0 ? "0" : legendInformations[0]?.radius}
                      />
                    ) : (
                      <g transform={path?.transform}>
                        <path
                          onClick={e => onSelectItem(e, path, sum)}
                          className="map_item_svg"
                          fill="#ECECEC"
                          strokeWidth="1px"
                          stroke="#FFF"
                          title={path?.title}
                          id={path?.id}
                          d={path?.d}
                        />
                      </g>
                    )
                  case "bulletNumber":
                    return path?.isCircle && legendInformations?.[0]?.color !== "#FFF" ? (
                      <>
                        <circle
                          fill={bulletColor || "#ECECEC"}
                          cx={path?.x}
                          cy={path?.y}
                          r={legendInformations[0]?.radius}
                        />
                        {sum >= 10 ? (
                          <text
                            x={path?.x - 9}
                            y={path?.y + 6}
                            fill={legendLabelColor}
                            fontWeight="700"
                          >
                            {sum}
                          </text>
                        ) : (
                          <text
                            x={path?.x - 5}
                            y={path?.y + 6}
                            fill={legendLabelColor}
                            fontWeight="700"
                          >
                            {sum}
                          </text>
                        )}
                      </>
                    ) : (
                      <g transform={path?.transform}>
                        <path
                          onClick={e => onSelectItem(e, path, sum)}
                          className="map_item_svg"
                          fill={legendInformations?.[0]?.color || "#ECECEC"}
                          stroke={legendInformations?.[0]?.color === "#FFF" ? "#006E87" : "#FFF"}
                          strokeWidth={path?.transform ? "15px" : "1px"}
                          title={path?.title}
                          id={path?.id}
                          d={path?.d}
                        />
                      </g>
                    )
                  case "bulletBckgrd":
                    return path?.isCircle && sum3 >= 1 ? (
                      <>
                        <circle
                          fill={bulletColor || "#ECECEC"}
                          cx={path?.x}
                          cy={path?.y}
                          r={6}
                          stroke="#FFF"
                          strokeWidth={bulletBorderSize}
                        />
                      </>
                    ) : (
                      <g transform={path?.transform}>
                        <path
                          onClick={e =>
                            onSelectItem(
                              e,
                              path,
                              sum2 >= 1
                                ? legendValue?.[1]?.name
                                : sum >= 1
                                ? legendValue?.[0]?.name
                                : legendValue?.[2]?.name
                            )
                          }
                          className="map_item_svg"
                          fill={
                            sum2 >= 1
                              ? legendValue?.[1]?.color
                              : sum >= 1
                              ? legendValue?.[0]?.color
                              : "#ECECEC"
                          }
                          stroke={legendValue?.[0]?.color === "#FFF" ? "#659DB0" : "#FFF"}
                          strokeWidth="1px"
                          title={path?.title}
                          id={path?.id}
                          d={path?.d}
                        />
                      </g>
                    )
                  case "filter":
                    var isPathSelected = path.codePostal.every(cp =>
                      filterListValues?.some(({ value }) => value === cp)
                    )
                    return (
                      <g transform={path?.transform}>
                        <path
                          onClick={e => onFilterChange(e, path)}
                          className="map_item_svg"
                          fill={isPathSelected ? primaryActionColor : "#cecece"}
                          strokeWidth="1px"
                          stroke="#FFF"
                          title={path?.title}
                          id={path?.id}
                          d={path?.d}
                        />
                      </g>
                    )
                  default:
                    return (
                      <g transform={path?.transform}>
                        <path
                          onClick={e => onSelectItem(e, path, sum)}
                          className="map_item_svg"
                          fill={
                            bulletView ? "#ECECEC" : legendInformations?.[0]?.color || "#ECECEC"
                          }
                          strokeWidth="1px"
                          stroke="#FFF"
                          title={path?.title}
                          id={path?.id}
                          d={path?.d}
                        />
                      </g>
                    )
                }
              })()
            })
          )}
        </g>
      </svg>
      {enableExportMenu && (
        <ExportMenu
          exportMenuPosition={exportMenuPosition}
          exportPNG={exportPNG}
          exportCSV={() => exportMap("csv")}
          exportXLSX={() => exportMap("xlsx")}
          exportPDF={() => exportMap("pdf")}
          exportImgPDF={getImgAndSaveAsPDF}
          exportMenuId={exportMenuId}
        />
      )}
      {visibilityLegend && (
        <Legende
          legendValue={legendValue}
          labelWeight={legendLabelWeight}
          labelColor={legendLabelColor}
          labelSize={legendLabelSize}
          bulletColor={bulletColor}
          mainLabel={legendLabel}
          isMobile={isMobile}
          mapVue={mapVue}
        />
      )}
    </MainContainer>
  )
}

MapSVG.propTypes = {
  primaryActionColor: PropTypes.string,
  legendLabelWeight: PropTypes.string,
  bulletBorderSize: PropTypes.string,
  legendLabelColor: PropTypes.string,
  legendLabelSize: PropTypes.string,
  enableExportMenu: PropTypes.bool,
  categoryData: PropTypes.string,
  tooltipColor: PropTypes.string,
  legendLabel: PropTypes.string,
  bulletColor: PropTypes.string,
  valueData: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  mapChoice: PropTypes.string,
  mapVue: PropTypes.string,
  widgetTraduction: PropTypes.object,
  visibilityLegend: PropTypes.bool,
  withChoicePath: PropTypes.bool,
  zoomAnimation: PropTypes.bool,
  zoomButtons: PropTypes.bool,
  isDraggable: PropTypes.bool,
  isZoomable: PropTypes.bool,
  bulletView: PropTypes.bool,
  dataChart: PropTypes.array,
  colorSetTheme: PropTypes.array,
  scaleDuration: PropTypes.number,
  scaleValue: PropTypes.number,
  legendSpecific: PropTypes.array,
  tooltipSpecificPRNSN: PropTypes.bool,
  mapPRNSN: PropTypes.bool,
}

MapSVG.defaultProps = {
  primaryActionColor: "#787878",
  bulletBorderSize: "1px",
  zoomAnimation: false,
  zoomButtons: false,
  enableExportMenu: false,
  withChoicePath: false,
  mapVue: "bckgrColor",
  widgetTraduction: {},
  categoryData: "",
  scaleDuration: 0.5,
  isDraggable: true,
  visibilityLegend: false,
  tooltipColor: "",
  bulletColor: "",
  scaleValue: 1.5,
  legendLabelWeight: "",
  legendLabelColor: "",
  legendLabelSize: "",
  legendLabel: "",
  valueData: [],
  mapChoice: "",
  isZoomable: false,
  bulletView: false,
  colorSetTheme: [
    "#68B8DC",
    "#6794DC",
    "#6771DC",
    "#8067DC",
    "#A367DC",
    "#C767DC",
    "#DC67CE",
    "#DC67AB",
    "#DC6788",
    "#DC6967",
    "#DC8C67",
    "#DCAF67",
  ],
  dataChart: [],
  legendSpecific: [],
  tooltipSpecificPRNSN: false,
  mapPRNSN: false,
}

export default MapSVG
